<!--
  Configure one blank.

  The sentence about the patient comes FIRST, above every control, because it is the answer to the
  only question a practice has about a document they have just uploaded. `fillMode` itself is never
  printed, and the two inspection booleans appear only inside the sentence that explains why a
  document is download-only.
-->
<h1 mat-dialog-title>{{ document.name }}</h1>

<div mat-dialog-content>
    <div class="pdd p-t-10">

        <p class="pdd__fill-mode">
            <mat-icon>{{ patientOutcomeIcon }}</mat-icon>
            <span data-test="editor-patient-outcome">{{ patientOutcomeSentence }}</span>
        </p>
        <p class="hint" *ngIf="downloadOnlyReason" data-test="editor-download-only-reason">
            {{ downloadOnlyReason }}
        </p>

        <div class="pdd__alert pdd__alert--info" data-test="not-yet-delivered">
            <mat-icon>info_outline</mat-icon>
            <div>{{ notYetDeliveredNotice }}</div>
        </div>

        <form [formGroup]="settingsForm">

            <mat-form-field appearance="outline" class="full-width">
                <mat-label>Name</mat-label>
                <input matInput formControlName="name" maxlength="300" data-test="settings-name">
            </mat-form-field>

            <!--
              CATEGORY — free text with SUGGESTIONS. See the upload dialog's copy of this field:
              the practice's own wording stays the rule, and what is offered is only what they have
              filed something under before, so the second admission form lands under the same word
              as the first.
            -->
            <mat-form-field appearance="outline" class="full-width">
                <mat-label>Category</mat-label>
                <input matInput formControlName="category" maxlength="100"
                    placeholder="e.g. Admission, Medical aid" data-test="settings-category"
                    [matAutocomplete]="categorySuggestions">
                <mat-autocomplete #categorySuggestions="matAutocomplete">
                    <mat-option *ngFor="let option of filteredCategories" [value]="option"
                        data-test="category-suggestion">{{ option }}</mat-option>
                </mat-autocomplete>
                <mat-hint>Your own wording. Used to group documents.</mat-hint>
            </mat-form-field>

            <mat-form-field appearance="outline" class="full-width">
                <mat-label>Description (for your staff, not the patient)</mat-label>
                <textarea matInput formControlName="description" rows="2"></textarea>
            </mat-form-field>

            <!--
              §14.2 - BOTH OF THESE ARE ABOUT A LINK, so neither is drawn for an information
              sheet. An information sheet is attached to an email and no token link is ever created
              for it (`patientDocumentRequestUtils.js:733`), so "how long a patient's link stays
              usable" describes a link that does not exist; and dedupe never blocks one, so "ask
              again after" describes a block that never happens. A control that promises behaviour
              the document cannot have is worse than no control.
            -->
            <div class="field-row" *ngIf="!informationOnly" data-test="link-timing">
                <mat-form-field appearance="outline">
                    <mat-label>Link expires after (days)</mat-label>
                    <input matInput type="number" formControlName="expiryDays" min="1" max="365">
                    <mat-hint>How long a patient's link stays usable.</mat-hint>
                </mat-form-field>
            </div>

            <!--
              §14.2 - WHAT THIS DOCUMENT ASKS OF THE PATIENT IS NOT SET HERE ANY MORE.

              It used to be a `requiresResponse` toggle at the top of this form, and the toggle was
              wrong in both directions. An information sheet is INFORMATION ONLY BY DEFINITION -
              that is what the section it lives in means - so offering "the patient completes it
              and sends it back" against one offers to make it something it is not; and a document
              in "documents that need a response" is one by the same definition. The kind is chosen
              once, by which section's add icon was clicked, and the upload dialog states its whole
              consequence at that moment.

              The kind is still SAID here, read-only, at the top of this dialog:
              `patientOutcomeSentence` is the first thing in the content. What left is the ability
              to change it by accident - a switch that silently moved a row between two sections of
              the tab behind it. A document of the wrong kind is deleted and re-uploaded into the
              other section, which is a deliberate act with a visible result.
            -->
            <!--
              §26 — WHO MAY ACT ON A RETURN OF THIS DOCUMENT.

              Two optional lists. Leaving one empty is the ordinary case and means anyone in the
              practice may do that step, so the hint says that rather than leaving an empty box to
              be interpreted.

              `[value]` binds `userId` — the USER's id, not the GroupUser row's. The component's
              own note explains at length why that one word is the highest-risk line in the
              feature: bind the wrong one and the permission looks configured and enforces nothing.

              Drawn only for a document that is ASKED BACK. Nothing is ever returned against an
              information sheet, so there is nothing to sign off and nothing to validate — the
              same reasoning that removed the link timings and the hard block from this dialog.
            -->
            <ng-container *ngIf="!informationOnly">
                <h3 class="m-t-20">Who reviews what comes back</h3>

                <mat-form-field appearance="outline" class="full-width">
                    <mat-label>Can sign off a return</mat-label>
                    <mat-select formControlName="signOffUserIds" multiple
                        data-test="sign-off-users">
                        <mat-option *ngFor="let member of groupMembers" [value]="member.userId">
                            {{ member.label }}
                        </mat-option>
                    </mat-select>
                    <mat-hint *ngIf="!hasDepartedMembers('signOffUserIds')">
                        Leave empty and anyone in the practice can sign one off.
                    </mat-hint>
                    <mat-hint *ngIf="hasDepartedMembers('signOffUserIds')" class="pdd__stale-hint"
                        data-test="sign-off-stale">
                        Someone named here has left the practice. While nobody named is still a
                        member, anyone can sign off — pick the people who should.
                    </mat-hint>
                </mat-form-field>

                <mat-form-field appearance="outline" class="full-width m-t-20">
                    <mat-label>Can validate onto the timeline</mat-label>
                    <mat-select formControlName="validationUserIds" multiple
                        data-test="validation-users">
                        <mat-option *ngFor="let member of groupMembers" [value]="member.userId">
                            {{ member.label }}
                        </mat-option>
                    </mat-select>
                    <mat-hint *ngIf="!hasDepartedMembers('validationUserIds')">
                        Leave empty and anyone in the practice can validate one.
                    </mat-hint>
                    <mat-hint *ngIf="hasDepartedMembers('validationUserIds')"
                        class="pdd__stale-hint" data-test="validation-stale">
                        Someone named here has left the practice. While nobody named is still a
                        member, anyone can validate — pick the people who should.
                    </mat-hint>
                </mat-form-field>
            </ng-container>

            <!-- A state unit sends nothing to patients, so none of these apply. -->
            <ng-container *ngIf="!stateMode">
            <h3 class="m-t-20">When patients should be asked for it</h3>

            <div class="toggle-container toggle-column">
                <mat-slide-toggle formControlName="sendWithInvite" color="primary"
                    data-test="send-with-invite">
                    Include it with the patient invite
                </mat-slide-toggle>
                <p class="hint">New patients are asked for it as part of onboarding.</p>
            </div>

            <!--
              §0 decision 4 — THE HARD BLOCK.

              The consequence is spelled out at the switch, and again, unmissably, once it is on. A
              practice is entitled to make this choice; they are not entitled to make it by accident.

              NOT DRAWN FOR AN INFORMATION SHEET. The block is `isStepValid` refusing to let the
              invite finish until the document has come BACK (§5), and nothing ever comes back from
              an information sheet - it is an attachment, not a step. Switched on against one, it
              was a setting a practice could reasonably believe was holding their patients at a step
              that does not exist.
            -->
            <ng-container *ngIf="!informationOnly">
                <div class="toggle-container toggle-column">
                    <mat-slide-toggle formControlName="requiredInInvite" color="primary"
                        data-test="required-in-invite">
                        Required - the invite cannot be finished without it
                    </mat-slide-toggle>
                    <p class="hint">
                        Only has an effect while the document is included with the invite.
                    </p>
                </div>

                <div class="pdd__alert pdd__alert--warn pdd__alert--indent"
                    *ngIf="requiredInInviteEnabled" data-test="required-in-invite-consequence">
                    <mat-icon>block</mat-icon>
                    <div>{{ requiredInInviteConsequence }}</div>
                </div>
            </ng-container>

            <!-- §14.6 decision 2. Configuration pre-ticks the send picker; it never forces. -->
            <div class="toggle-container toggle-column">
                <mat-slide-toggle formControlName="sendWithConsentRequest" color="primary"
                    data-test="send-with-consent-request">
                    Include it when a consent request is sent
                </mat-slide-toggle>
                <p class="hint">
                    Ticked for staff by default on a consent request. They can untick it per patient.
                </p>
            </div>

            <div class="toggle-container toggle-column">
                <mat-slide-toggle formControlName="allowManualSend" color="primary"
                    data-test="allow-manual-send">
                    Let staff send it by hand from the patient file
                </mat-slide-toggle>
            </div>
            </ng-container>

            <div class="toggle-container toggle-column">
                <mat-slide-toggle formControlName="active" color="primary">Active</mat-slide-toggle>
                <p class="hint">An inactive document stays here but is not offered anywhere.</p>
            </div>
        </form>

        <!-- ── The file ──────────────────────────────────────────────────────────────────── -->
        <h3 class="m-t-20">The file</h3>

        <p class="hint" data-test="file-facts">
            {{ document.mediaFileName }}<ng-container *ngIf="pageCountLabel"> &middot;
                {{ pageCountLabel }}</ng-container><ng-container *ngIf="fileSizeLabel"> &middot;
                {{ fileSizeLabel }}</ng-container>
        </p>

        <div class="pdd__alert pdd__alert--info" data-test="replace-notice">
            <mat-icon>autorenew</mat-icon>
            <div>{{ replaceFileNotice }}</div>
        </div>

        <p class="hint" data-test="replace-caps">
            PDF only, up to {{ choice.maxFileMb }} MB and {{ choice.maxPages }} pages.
        </p>

        <!-- The same house picker, and the same four load-bearing inputs. See the upload
             dialog's template for why each of them is not taste. -->
        <div class="pdd__picker">
            <app-file-upload class="full-width" [fileObjects]="[]" [allowMultiple]="false"
                [allowedFileTypes]="'application/pdf'" [showGallery]="false" [shouldRefresh]="true"
                [pasteEnabled]="false" title="" (fileUpload)="onFilesPicked($event)"
                hintMessage="Drag the replacement PDF here, or click to choose one">
            </app-file-upload>

            <span class="pdd__chosen" *ngIf="choice.file" data-test="chosen-replacement">
                <mat-icon>picture_as_pdf</mat-icon>
                <strong>{{ choice.file.name }}</strong>
                <span class="pdd__dim">{{ megabytes(choice.file.size) }} MB</span>
                <span class="pdd__dim" *ngIf="choice.pageCount !== null">
                    {{ choice.pageCount }} page(s)
                </span>
                <button mat-icon-button type="button" aria-label="Remove the chosen replacement"
                    (click)="clearReplacementFile()">
                    <mat-icon>close</mat-icon>
                </button>
            </span>

            <button mat-stroked-button color="primary" type="button" data-test="replace-file"
                [disabled]="isReplacingFile || !choice.file" (click)="replaceFile()">
                <mat-icon>autorenew</mat-icon> Replace the file
            </button>
        </div>

        <!-- Held on screen against the picker, never a toast. -->
        <div class="pdd__alert pdd__alert--error" *ngIf="choice.error" data-test="replacement-error">
            <mat-icon>error_outline</mat-icon>
            <div>{{ choice.error }}</div>
        </div>
    </div>
</div>

<div mat-dialog-actions class="dialog-actions">
    <button mat-button type="button" data-test="close-dialog" (click)="close()">Close</button>
    <button mat-raised-button color="primary" type="button" data-test="save-settings"
        [disabled]="isSavingSettings" (click)="saveSettings()">Save</button>
</div>
